import { type Page, typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

export type { Page };

export interface RevisionSummary {
  id: string;
  model_id: string;
  revision_no: number;
  status: "training" | "active" | "archived" | "discarded" | "failed" | "draft";
  metrics: Record<string, number | string | null> | null;
  created_at: string;
  training_summary?: { eval_metric?: string | null; [key: string]: unknown } | null;
  decision_threshold?: number | null;
  decision_threshold_metric?: string | null;
  decision_threshold_calibrated_at?: string | null;
}

export interface ModelSummary {
  id: string;
  project_id: string;
  name: string;
  task_type: string;
  status: string;
  active_revision_id: string | null;
  latest_revision_id: string | null;
  created_at: string;
  last_accessed_at: string;
}

export interface ModelDetail extends ModelSummary {
  recipe_id: string | null;
  active_revision: RevisionSummary | null;
  latest_revision: RevisionSummary | null;
  total_revisions: number;
}

export function useModels(
  projectId: string | null,
  params: { page?: number; pageSize?: number } = {},
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery({
    enabled: !!projectId,
    queryKey: ["models", projectId, page, pageSize],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/models", {
        params: {
          // Justified: list models schema lacks page/page_size params; backend accepts them
          query: { project_id: projectId ?? "", page, page_size: pageSize } as {
            project_id: string;
          },
        },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: Page_ModelSummary_ is structurally compatible but local ModelSummary adds last_accessed_at
      return data as unknown as Page<ModelSummary>;
    },
    placeholderData: (prev) => prev,
  });
}

export function useModel(id: string) {
  return useQuery({
    enabled: !!id,
    queryKey: ["models", id],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/models/{model_id}", {
        params: { path: { model_id: id }, query: { include_details: true } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: model GET response is {[key:string]:unknown} in openapi schema
      return data as unknown as ModelDetail;
    },
  });
}

export function useRevisions(modelId: string, params: { page?: number; pageSize?: number } = {}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery({
    enabled: !!modelId,
    queryKey: ["models", modelId, "revisions", page, pageSize],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/models/{model_id}/revisions", {
        params: {
          path: { model_id: modelId },
          // Justified: revisions schema lacks page/page_size params; backend accepts them
          query: { page, page_size: pageSize } as { include_details?: boolean },
        },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: Page_RevisionSummary_ is compatible but local type adds decision_threshold fields
      return data as unknown as Page<RevisionSummary>;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCompare(modelId: string, a: number | null, b: number | null) {
  return useQuery({
    enabled: a != null && b != null,
    queryKey: ["models", modelId, "compare", a, b],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/models/{model_id}/revisions/compare", {
        params: {
          path: { model_id: modelId },
          query: { a: a ?? undefined, b: b ?? undefined },
        },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: compare response is {[key:string]:unknown} in openapi schema
      return data as unknown as {
        a: RevisionSummary & { metrics: Record<string, number> };
        b: RevisionSummary & { metrics: Record<string, number> };
      };
    },
  });
}

export interface LeaderboardEntry {
  name: string;
  score_val: number | null;
  fit_time_sec?: number | null;
  pred_time_seconds?: number | null;
  stack_level?: number;
}

export interface LeaderboardResponse {
  entries?: LeaderboardEntry[];
  detectors?: Array<{ detector: string; n_flagged: number; agreement: number }>;
  metric_name?: string;
  metric_type?: "percentage" | "absolute" | null;
  eval_metric?: string;
}

export interface FeatureImportanceItem {
  feature: string;
  importance: number;
  p_value?: number;
}

export interface FeatureImportanceResponse {
  family: string;
  feature_importance: FeatureImportanceItem[];
}

export interface ForecastDiagnostics {
  strategy?: string;
  num_val_windows?: number;
  train_rows?: number;
  per_group: Array<{
    group_id: string;
    metric_score: number | null;
    n_obs: number;
    insufficient_history?: boolean;
  }>;
  truncated_at?: number;
}

export function useLeaderboard(modelId: string, revNo: number | null) {
  return useQuery({
    enabled: !!modelId && revNo != null,
    queryKey: ["models", modelId, "revisions", revNo, "leaderboard"],
    queryFn: async () => {
      const { data, error } = await typedApi.GET(
        "/v1/models/{model_id}/revisions/{rev_no}/leaderboard",
        {
          params: { path: { model_id: modelId, rev_no: revNo as number } },
        },
      );
      if (error) throw new Error(JSON.stringify(error));
      // Justified: leaderboard response is {[key:string]:unknown} in openapi schema
      return data as unknown as LeaderboardResponse;
    },
  });
}

export function useFeatureImportance(modelId: string, revNo: number | null) {
  return useQuery({
    enabled: !!modelId && revNo != null,
    queryKey: ["models", modelId, "revisions", revNo, "feature_importance"],
    queryFn: async () => {
      const { data, error } = await typedApi.GET(
        "/v1/models/{model_id}/revisions/{rev_no}/feature_importance",
        { params: { path: { model_id: modelId, rev_no: revNo as number } } },
      );
      if (error) throw new Error(JSON.stringify(error));
      // Justified: feature_importance response is {[key:string]:unknown} in openapi schema
      return data as unknown as FeatureImportanceResponse;
    },
    retry: false,
  });
}

export function useForecastDiagnostics(modelId: string, revNo: number | null) {
  return useQuery({
    enabled: !!modelId && revNo != null,
    queryKey: ["models", modelId, "revisions", revNo, "forecast_diagnostics"],
    queryFn: async () => {
      const { data, error } = await typedApi.GET(
        "/v1/models/{model_id}/revisions/{rev_no}/forecast_diagnostics",
        { params: { path: { model_id: modelId, rev_no: revNo as number } } },
      );
      if (error) throw new Error(JSON.stringify(error));
      // Justified: forecast_diagnostics response is {[key:string]:unknown} in openapi schema
      return data as unknown as ForecastDiagnostics;
    },
    retry: false,
  });
}
